// NodePercentageModal.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// CSS
import './NodePercentageModal.css';

// Components
import Button from 'components/button/Button';
import ModalButtons from 'components/modal/buttons/ModalButtons';
import ModalForm from 'components/modal/form/ModalForm';
import Modal from 'components/modal/Modal';
import Slider from 'components/slider/Slider';

const clampPercentage = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  if (numeric < 0) return 0;
  if (numeric > 100) return 100;
  return Math.round(numeric);
};

const normalizeSnapTo = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 5;
  return Math.round(parsed);
};

/**
 * NodePercentageModal
 *
 * @returns {ReactNode} The rendered component.
 */
const NodePercentageModal = ({ label, value, snapTo, isOpen, setOpen, onChange }) => {

  // ----- STATE ----- //

  const [draftValue, setDraftValue] = useState(0);

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- MEMOS ----- //

  const resolvedSnapTo = useMemo(() => normalizeSnapTo(snapTo), [snapTo]);

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (isOpen) {
      setDraftValue(clampPercentage(value));
    }
  }, [isOpen, value]);

  // ----- MEMOS ----- //

  const modalTitle = useMemo(() => label || 'Percentage', [label]);

  // ----- HANDLERS ----- //

  /**
 * Handles save.
 */

  const handleSave = () => {
    if (typeof onChange === 'function') {
      onChange(clampPercentage(draftValue));
    }
    setOpen(false);
  };

  // ----- RENDER ----- //

  return (
    <Modal
      title={modalTitle}
      isOpen={isOpen}
      width={isMobile ? '100%' : 420}
      height="auto"
      onClose={() => setOpen(false)}
    >
      <ModalForm>

        {/* WRAPPER */}
        <div className="node-percentage-modal-wrapper">
          <Slider
            value={draftValue}
            min={0}
            max={100}
            snapTo={resolvedSnapTo}
            suffix="%"
            onChange={setDraftValue}
          />
        </div>
      </ModalForm>

      <ModalButtons>
        <Button label="Cancel" onClick={() => setOpen(false)} />
        <Button label="Save" onClick={handleSave} />
      </ModalButtons>
    </Modal>
  );
};

// Dev Tools
NodePercentageModal.displayName = 'NodePercentageModal';

export default NodePercentageModal;
